/**
 * Mount-on-hover animated poster or muted trailer preview.
 * Idle cards keep only the static thumbnail mounted.
 */

import Image from "next/image";
import type React from "react";
import { useEffect } from "react";

type CardHoverPreviewProps = {
  isActive: boolean;
  animatedPoster?: string;
  trailerUrl?: string;
  captionsUrl?: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  alt: string;
  videoClassName: string;
  trailerId?: string;
  onReady: () => void;
};

/**
 * Renders animated GIF or trailer video only while the card hover preview is active.
 */
export function CardHoverPreview({
  isActive,
  animatedPoster,
  trailerUrl,
  captionsUrl,
  videoRef,
  alt,
  videoClassName,
  trailerId,
  onReady,
}: Readonly<CardHoverPreviewProps>) {
  useEffect(() => {
    if (!isActive || animatedPoster || trailerUrl) return;
    onReady();
  }, [isActive, animatedPoster, trailerUrl, onReady]);

  if (!isActive) return null;

  if (animatedPoster) {
    return (
      <Image
        className="pointer-events-none absolute top-0 left-0 z-[1] h-full w-full object-cover"
        src={animatedPoster}
        alt={alt}
        fill
        unoptimized
        onLoadingComplete={onReady}
      />
    );
  }

  if (!trailerUrl) return null;

  return (
    <video
      ref={videoRef}
      className={`pointer-events-none ${videoClassName}`}
      src={trailerUrl}
      id={trailerId}
      loop
      playsInline
      muted
      preload="auto"
      onLoadedData={onReady}
      onCanPlay={onReady}
    >
      {captionsUrl ? (
        <track kind="captions" src={captionsUrl} srcLang="en" label="English" />
      ) : null}
    </video>
  );
}

/** True when the pointer device supports real hover (excludes touch/mobile). */
export function canUseHoverPreview(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/** True when the card has hoverable animated poster or trailer media. */
export function hasHoverPreviewSource(video: {
  trailer?: {
    file_url?: string | null;
    video_posters?: Array<{ video_poster_type?: string; url?: string }>;
  };
}): boolean {
  const animatedPoster = video.trailer?.video_posters?.find(
    (p) => p.video_poster_type === "animated"
  )?.url;
  return Boolean(animatedPoster || video.trailer?.file_url);
}

/** Resolves animated poster URL from trailer posters, if present. */
export function getAnimatedPosterUrl(video: {
  trailer?: {
    video_posters?: Array<{ video_poster_type?: string; url?: string }>;
  };
}): string | undefined {
  return video.trailer?.video_posters?.find((p) => p.video_poster_type === "animated")?.url;
}
